import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useEffect, useCallback } from 'react';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { practiceSessionTypes } from '@actions-constants/index';
import {
  fetchPracticeTopologies,
  fetchInCorrectPracticeTopologies,
  fetchPYQFilters,
  fetchPYQTopologies
} from '@actions/practiceSession';
import { getPracticeAvailableLanguages } from '@selectors/practiceSession';
import usePracticeStore from '@stores/practice';
import { PRACTICE_TYPE } from '../../constants';

const EMPTY_ARRAY = [];

const useConceptFetch = (currentFilter) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { goalUID } = router.query;
  const { practiceSelectedTopic } = usePracticeStore();

  const selectedTopic = practiceSelectedTopic || {};

  const filters = useSelectorWithShallowEqual(
    ({ practiceSession }) =>
      practiceSession.filters[practiceSelectedTopic.uid] || EMPTY_ARRAY
  );

  const languageData = useSelectorWithShallowEqual(
    getPracticeAvailableLanguages(goalUID)
  );

  const { open: showSnackbar } = useSnackbar();

  const handleError = useCallback(() => {
    showSnackbar({
      message: 'Unable to fetch data. Please try again',
      type: 'error'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.length || selectedTopic?.type !== PRACTICE_TYPE.PYQ) {
      return;
    }

    dispatch(fetchPYQFilters(selectedTopic.uid)).then((response) => {
      if (
        response.type ===
        `${practiceSessionTypes.FETCH_PRACTICE_PYQ_FILTERS}_FAILURE`
      ) {
        handleError();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters.length, selectedTopic?.type, selectedTopic.uid]);

  useEffect(() => {
    if (!selectedTopic.type) {
      return;
    }

    const fetchTopologies = () => {
      if (selectedTopic.type === PRACTICE_TYPE.INCORRECT) {
        return dispatch(fetchInCorrectPracticeTopologies(selectedTopic.uid));
      }

      if (
        selectedTopic.type === PRACTICE_TYPE.FULL ||
        selectedTopic.type === PRACTICE_TYPE.EACH_TOPIC
      ) {
        return dispatch(
          fetchPracticeTopologies({
            uid: selectedTopic.uid,
            showSubSections: false,
            showAllChildren: true,
            languageCode: languageData?.user_preference ?? 0
          })
        );
      }
    };

    fetchTopologies()?.then((response) => {
      if (
        response.type ===
        `${practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES}_FAILURE`
      ) {
        handleError();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTopic.type, selectedTopic.uid]);

  useEffect(() => {
    if (!selectedTopic.type || selectedTopic.type !== PRACTICE_TYPE.PYQ) {
      return;
    }

    dispatch(
      fetchPYQTopologies({
        uid: selectedTopic.uid,
        examUID: currentFilter.examUID,
        startYear: currentFilter.startYear,
        endYear: currentFilter.endYear
      })
    ).then((response) => {
      if (
        response.type ===
        `${practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES}_FAILURE`
      ) {
        handleError();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentFilter]);
};

export default useConceptFetch;
